import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { m as motion, useReducedMotion } from "framer-motion";
import { Magnetic } from "./MagneticButton";

import { GrainOverlay } from "./GrainOverlay";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";

export type SignatureCtaProductContext = "pulse" | "prime" | "pilot" | "neutral";

interface Props {
  eyebrow?: string;
  title?: string;
  body?: string;
  tone?: "peach" | "mint" | "butter";
  className?: string;
  /** Drives default copy when title/body aren't provided. Defaults to "pulse" (legacy). */
  productContext?: SignatureCtaProductContext;
}

const toneStyles = {
  peach: {
    bg: "from-peach/20 via-peach/5 to-transparent",
    ring: "ring-peach/30",
    btn: "bg-peach text-peach-foreground hover:shadow-peach/40",
    accent: "text-peach",
  },
  mint: {
    bg: "from-mint/20 via-mint/5 to-transparent",
    ring: "ring-mint/30",
    btn: "bg-mint text-mint-foreground hover:shadow-mint/40",
    accent: "text-mint",
  },
  butter: {
    bg: "from-butter/20 via-butter/5 to-transparent",
    ring: "ring-butter/30",
    btn: "bg-butter text-black hover:shadow-butter/40",
    accent: "text-butter",
  },
} as const;

const DEFAULT_BODY: Record<SignatureCtaProductContext, { sv: string; en: string }> = {
  pulse: {
    sv: "Kostnadsfritt strategimöte. Vi går igenom var ni är, vad ni vill, och designar er Pulse tillsammans.",
    en: "Free strategy call. We'll talk through where you are, what you want, and design your Pulse together.",
  },
  prime: {
    sv: "Kostnadsfritt 30-minutersmöte. Vi går igenom idé, mål och budget — och säger ärligt om Prime är rätt produktion.",
    en: "Free 30-minute call. We walk through idea, goals and budget — and tell you honestly if Prime is the right fit.",
  },
  pilot: {
    sv: "Kostnadsfritt möte. Vi skissar en Pilot — en första produktion utan bindning — och visar hur upplägget kan se ut.",
    en: "Free call. We sketch a Pilot — a first production without commitment — and show what the setup could look like.",
  },
  neutral: {
    sv: "Kostnadsfritt strategimöte. Vi går igenom var ni är, vad ni vill, och föreslår rätt nästa steg.",
    en: "Free strategy call. We walk through where you are, what you want, and suggest the right next step.",
  },
};

/** The signature booking CTA — used at the bottom of every wow-page as the red thread. */
export function SignatureCta({
  eyebrow,
  title,
  body,
  tone = "peach",
  className,
  productContext = "pulse",
}: Props) {
  const reduce = useReducedMotion();
  const { lang } = useLanguage();
  const en = lang === "en";
  const _eyebrow = eyebrow ?? (en ? "Next step" : "Nästa steg");
  const _title = title ?? (en ? "Book 30 minutes. We'll handle the rest." : "Boka 30 minuter. Vi tar resten.");
  const _body = body ?? DEFAULT_BODY[productContext][en ? "en" : "sv"];
  const ctaTo = en ? "/en/contact" : "/kontakt";
  const ctaLabel = en ? "Book strategy call" : "Boka strategimöte";
  const s = toneStyles[tone];

  return (
    <section className={cn("relative overflow-hidden py-24 md:py-32", className)}>
      <div className={cn("absolute inset-0 bg-gradient-to-b", s.bg)} aria-hidden />
      <GrainOverlay opacity={0.05} />
      <div className="container-wide relative">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "mx-auto max-w-4xl rounded-[2.5rem] border border-white/10 bg-surface/60 p-10 ring-1 backdrop-blur-md md:p-16",
            s.ring,
          )}
        >
          <div className={cn("text-xs uppercase tracking-[0.22em]", s.accent)}>{_eyebrow}</div>
          <h2 className="mt-5 text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
            {_title}
          </h2>
          <p className="mt-6 max-w-2xl text-pretty text-base text-muted-foreground md:text-lg">
            {_body}
          </p>
          <div className="mt-10 flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <Magnetic strength={0.3}>
              <Link
                to={ctaTo}
                className={cn(
                  "group inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold transition-shadow hover:shadow-[0_20px_60px_-12px]",
                  s.btn,
                )}
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
